import { memo, useCallback, useRef, type FocusEvent } from 'react'
import cn from 'classnames'
import { Drawer } from 'vaul'

import { onKeyboardSettled, type Unsubscribe } from '~/shared/platform'

import { KEYBOARD_SETTLE_TIMEOUT_MS } from './config'
import type { BottomSheetProps, BottomSheetSlotProps } from './types'
import { isTextField, scrollFieldIntoView } from './utils'

import styles from './BottomSheet.module.scss'

// Шапка и футер нетронуты драг-атрибутом — по ним шторка должна тянуться вниз наравне с
// грабером (фидбек основателя). `vaul` начинает закрывающий жест только после реального сдвига
// пальца (порог 10px на тач / 2px на мышь), поэтому обычный тап по кнопке в футере
// не превращается в драг и кнопки продолжают нажиматься.
const Header = memo(function Header(props: BottomSheetSlotProps) {
  return <div className={styles.header}>{props.children}</div>
})

// Контент — уже нативно скроллируемая зона (`@include scroll-area` в модуле стилей), а `vaul`
// сам умеет отличать «скроллим список» от «тянем шторку»: пока `scrollTop !== 0`, закрывающий
// жест не стартует. Explicit `data-vaul-no-drag` тут не нужен. Пикеры-барабаны внутри —
// исключение: они не используют нативный скролл, поэтому сами помечают себя этим атрибутом
// (см. WheelPicker/WheelPicker.tsx), как и скроллируемые списки без нативного overflow
// (см. CityPickerSheet).
const Content = memo(function Content(props: BottomSheetSlotProps) {
  const ref = useRef<HTMLDivElement>(null)

  // Скролл к сфокусированному полю внутри шторки — наш: система его не делает (§7 telegram.md),
  // а собственная поправка vaul выключена (`repositionInputs={false}`, см. ниже).
  //
  // Считать положение поля можно только тогда, когда шторка уже знает свою высоту при
  // клавиатуре. Раньше здесь ждали первый `resize` visualViewport'а — а он приходит посреди
  // анимации, когда высота ещё старая. Ждём `onKeyboardSettled`: рантайм зовёт его ровно
  // после того, как опубликовал устоявшуюся геометрию. Если про клавиатуру так и не сказали
  // (внешняя клавиатура, десктоп, переход между полями) — досчитываем по таймауту.
  const handleFocus = useCallback((event: FocusEvent<HTMLDivElement>) => {
    const container = ref.current
    const field = event.target

    if (!container || !isTextField(field)) return

    let isDone = false
    let unsubscribe: Unsubscribe = () => {}

    const scroll = () => {
      if (isDone) return
      isDone = true
      unsubscribe()
      scrollFieldIntoView(container, field)
    }

    unsubscribe = onKeyboardSettled(scroll)
    window.setTimeout(scroll, KEYBOARD_SETTLE_TIMEOUT_MS)
  }, [])

  return (
    <div ref={ref} className={styles.content} onFocus={handleFocus}>
      {props.children}
    </div>
  )
})

const Footer = memo(function Footer(props: BottomSheetSlotProps) {
  return <div className={styles.footer}>{props.children}</div>
})

/**
 * Главный оверлей продукта: всё модальное в «Луне» — bottom sheet (спека 01 §7).
 *
 * Под капотом — `vaul` (поверх Radix Dialog): свайп-закрытие с инерцией, фокус-трап,
 * блокировка скролла под шторкой и корректное поведение на iOS. Свою реализацию на
 * pointer-событиях убрали: она не давала нормального жеста и ломала скролл внутри шторки.
 *
 * Закрыть свайпом можно за грабер, шапку, футер и любую нескроллируемую часть контента —
 * `handleOnly` намеренно не используем. Зоны, где жест должен управлять содержимым, а не
 * шторкой (барабаны в WheelPicker, несколлящийся программно список в CityPickerSheet),
 * помечают себя атрибутом `data-vaul-no-drag` сами, на своём корневом узле.
 *
 * Compound-структуру собирает потребитель: BottomSheet.Header / .Content / .Footer.
 */
const BottomSheetRoot = memo(function BottomSheet(props: BottomSheetProps) {
  const { isOpen, onClose, size = 'AUTO', children } = props

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
      // Тёмная тема продукта — единственная, системную подстройку фона отключаем.
      shouldScaleBackground={false}
      // Клавиатуру шторка отрабатывает НЕ средствами vaul. Его `repositionInputs` меряет её
      // сам — по `window.innerHeight` и `visualViewport.height` — и пишет шторке инлайновую
      // `height`. В вебвью Telegram на iOS `innerHeight` не равен окну мини-аппы, поэтому его
      // поправка приезжала поверх нашей (`bottom: var(--layout-bottom-lift)`), клавиатура
      // вычиталась дважды, и шторка улетала под потолок — тем самым «всё сильно уезжает вверх».
      // Вместе с ним выключается и его iOS-блокировка скролла (transform у полей, `scrollTo`
      // по всей странице) — нам она не нужна: у нас `body { overflow: hidden }`.
      repositionInputs={false}
    >
      <Drawer.Portal>
        <Drawer.Overlay className={styles.overlay} />
        <Drawer.Content className={cn(styles.sheet, styles[`size_${size}`])}>
          {/* Радикс требует заголовок для скринридера; визуальный заголовок кладёт потребитель. */}
          <Drawer.Title className={styles.srOnly}>Шторка</Drawer.Title>
          <div className={styles.graberZone}>
            <Drawer.Handle className={styles.graber} />
          </div>
          {children}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
})

export const BottomSheet = Object.assign(BottomSheetRoot, { Header, Content, Footer })
